import {
  Component,
  Event,
  EventEmitter,
  Prop,
  h,
  ComponentInterface,
} from '@stencil/core';
import type { DDCProps } from '@pes-ui/components/dist/types/globals/ddc';
import { TCustomPrice, TPesCatalogPageType, TProductCtas, CAASContentOverviewSlotName, productCtas, siteType, PES_USER, FeatureStatus, getLookup, getBrandName } from '@pes-ui/utils';

import { TSiteType } from '@pes-ui/components/dist/types/globals/types';

import type { TSearchParams } from '@pes-ui/utils';
import { initExpAPIEndpointURLBuilder } from '../../../globals/utils/exp-api-urls-builder';
import { MY_SCHNEIDER_CLICK_EVENT } from '../../../globals/constants';

const defaultFeatures = {
  productCtas,
};

export type TFeatures = {
  productCtas?: TProductCtas;
};

@Component({
  tag: 'pes-spryker-container',
})
export class PesSprykerContainer implements ComponentInterface {
  /**
   * Rest API host.
   */
  @Prop() host: string;

  /**
   * Language code.
   * @example en or fr.
   */
  @Prop({ mutable: true }) language = 'en';

  /**
   * Country code.
   * @example us or ww or fr.
   */
  @Prop({ mutable: true }) country = 'us';

  /**
   * Catalog level page to show.
   */
  @Prop({ mutable: true }) page: TPesCatalogPageType = '';

  /**
   * Catalog page id.
   */
  @Prop({ mutable: true }) pageId: string;

  /**
   * Show custom price for all pages.
   */
  @Prop({ mutable: true }) showCustomPrice = false;

  /**
   * Show PES price for all pages.
   */
  @Prop({ mutable: true }) showCatalogPrice = true;

  /**
   * Custom price data.
   */
  @Prop() customPrice: TCustomPrice;

  /**
   * Callback function for Breadcrumbs
   * @todo update with event to use instead and mark as deprecated.
   */
  @Prop() returnBreadcrumbs: (list: unknown[]) => void;

  /**
   * If show breadcrumbs on pages.
   */
  @Prop() showBreadcrumbs = false;

  /**
   * Determines which version of the label to display, short or long.
   */
  @Prop() substitutionLabelShort: boolean;

  /**
   * Tab id for scroll to a tab on PDP.
   * @example documents
   */
  @Prop({ mutable: true }) tabId?: string;

  /**
   * If show Documents CTA and documents list in product-card
   */
  @Prop() showDocuments?: boolean;

  /**
   * PES Catalogue features
   */
  @Prop() features: TFeatures = defaultFeatures;

  /**
   * Defines whether to show Buy Online button
   */
  @Prop({ mutable: true }) showBuyOnline = false;

  /**
   * Defines whether to show compare button
   */
  @Prop({ mutable: true }) showCompare = false;

  /**
   * Defines whether to show FAQ section
   */
  @Prop({ mutable: true }) showFaq = false;

  /**
   * Defines whether to show Recently Viewed section
   */
  @Prop({ mutable: true }) showRecentlyViewed = false;

  /**
   * Defines the context of the app: B2B or B2C
   */
  @Prop({ mutable: true }) siteType: TSiteType = siteType.B2B;

  /**
   * Object with url search parameters if exists
   * @example { filter: "business-10-smart-grid" }
   */
  @Prop({ mutable: true }) searchParams: TSearchParams = {};

  /**
   * This parameter contains configuration for the document listing component (DDC App Source).
   * For more information, please visit
   * https://confluence.se.com/pages/viewpage.action?pageId=128510823 or https://confluence.se.com/display/DCP/DC+WC+1.57.0+-+Integration+Guideline+Document_Listing.
   */
  @Prop() ddcProps?: Partial<DDCProps>;

  /**
   * This parameter contains configuration for the document download center url
   * displayed as an action block on All Products page.
   * For more information, please visit
   * https://confluence.se.com/display/PESV3/SE+All+Products+component.
   */
  @Prop() ddcAllProductsUrl?: string;

  @Prop() productCtas: TProductCtas;

  @Prop() featureFlags: Record<string, FeatureStatus>;

  /**
   * Fired on pes-link click.
   * Contains page, pageId  parameters as event.detail.
   */
  @Event() pesLinkClick: EventEmitter;

  // TODO i'm not sure if we need to keep businessId and userType
  // it looks like unused params and can be removed because only subcategory page can use it.
  private endpoint = 'product/';

  /**
   * Customizable background color for each page.
   */
  backgroundColor;

  constructor() {
    window.isV3 = true;
    window.isPublicPes = true;
    window.isIndexDBEnabled = this.showBreadcrumbs;
  }

  componentWillLoad(): void {
    window.addEventListener(MY_SCHNEIDER_CLICK_EVENT, this.handleNavigationChange);

    initExpAPIEndpointURLBuilder({
      languageCode: this.language,
      countryCode: this.country,
      host: this.host,
      pathName: this.endpoint,
      brand: getBrandName(),
    });
    const userToken = this.ddcProps?.idmsToken;
    if (userToken) sessionStorage.setItem(PES_USER.TOKEN_KEY, userToken);
    getLookup(this.host, this.country, this.language);
  }

  disconnectedCallback(): void {
    window.removeEventListener(MY_SCHNEIDER_CLICK_EVENT, this.handleNavigationChange);
  }

  handleNavigationChange = ({ detail }: CustomEvent<any>) => {
    this.pageId = detail.pageId;
    this.page = detail.page;
    this.tabId = detail.tabId;
    this.searchParams = detail.searchParams;
  };

  renderPage(): HTMLElement {
    const commonProps = {
      host: this.host,
      endpoint: this.endpoint,
      country: this.country,
      language: this.language,
    };

    switch (this.page) {
      case 'category': {
        return (
          <pes-app-spryker-category-container
            categoryId={this.pageId}
            host={this.host}
            countryCode={this.country}
            languageCode={this.language}
            showBreadcrumbs={this.showBreadcrumbs}
            showBuyOnline={this.showBuyOnline}
            showCompare={this.showCompare}
            showDocuments={this.showDocuments}
            showRecentlyViewed={this.showRecentlyViewed}
            enableCommerceConnector={true}
            searchParams={this.searchParams}
            productCtas={this.productCtas}
          />
        );
      }
      case 'subcategory': {
        return (
          <pes-app-spryker-subcategory-container
            host={this.host}
            countryCode={this.country}
            languageCode={this.language}
            subcategoryId={this.pageId}
            showBreadcrumbs={this.showBreadcrumbs}
            showBuyOnline={this.showBuyOnline}
            showCompare={this.showCompare}
            showDocuments={this.showDocuments}
            showRecentlyViewed={this.showRecentlyViewed}
            enableCommerceConnector={true}
            productCtas={this.productCtas}
          />
        );
      }
      case 'range': {
        return (
          <pes-app-spryker-range-container
            host={this.host}
            countryCode={this.country}
            endpoint={this.endpoint}
            languageCode={this.language}
            rangeId={this.pageId}
            showBreadcrumbs={this.showBreadcrumbs}
            showDocuments={this.showDocuments}
            productCtas={this.productCtas}
            showBuyOnline={this.showBuyOnline}
            siteType={this.siteType}
            showRecentlyViewed={this.showRecentlyViewed}
            documentListing={this.ddcProps}
          >
            <slot slot={CAASContentOverviewSlotName} name={CAASContentOverviewSlotName} />
          </pes-app-spryker-range-container>
        );
      }
      case 'product': {
        return (
          <pes-app-spryker-pdp-container
            host={this.host}
            countryCode={this.country}
            languageCode={this.language}
            productId={this.pageId}
            tabId={this.tabId}
            productCtas={this.productCtas}
            showBreadcrumbs={this.showBreadcrumbs}
            showCustomPrice={this.showCustomPrice}
            showCatalogPrice={this.showCatalogPrice}
            showBuyOnline={this.showBuyOnline}
            showCompare={this.showCompare}
            showFaq={this.showFaq}
            showRecentlyViewed={this.showRecentlyViewed}
            enableCommerceConnector={true}
            customPrice={this.customPrice}
            documentListing={this.ddcProps}
            featureFlags={this.featureFlags}
          >
            <slot slot="discounted-price" name="discounted-price" />
          </pes-app-spryker-pdp-container>
        );
      }
      default: {
        return (
          <pes-app-spryker-all-products-container
            {...commonProps}
            showBreadcrumbs={this.showBreadcrumbs}
            ddcAllProductsUrl={this.ddcAllProductsUrl}
          />
        );
      }
    }
  }

  setBackgroundColor(): void {
    switch (this.page) {
      case 'subcategory':
        this.backgroundColor = '#FFF';
        break;
      case 'category':
        this.backgroundColor = '#FAFAFA';
        break;
      default:
        this.backgroundColor = '#F7F7F7';
    }
  }

  render(): void {
    this.setBackgroundColor();
    return (
      <div
        style={{
          // eslint-disable-next-line no-restricted-globals
          'min-height': `${innerHeight}px`,
          'align-items': 'flex-start',
          'background-color': this.backgroundColor,
        }}
      >
        {this.renderPage()}
      </div>
    );
  }
}
